import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  tableCellClasses,
} from "@mui/material";
import Colors from "assets/Style/Colors";
import { FontFamily } from "assets";
import { ErrorToaster } from "components/Toaster";
import styled from "@emotion/styled";
import moment from "moment/moment";
import { useLocation, useParams } from "react-router-dom";
import CurrencyServices from "services/Currency";
import { PrimaryButton } from "components/Buttons";
import { PDFExport } from "@progress/kendo-react-pdf";
import { CommaSeparator, handleExportWithComponent } from "utils";
import ShippingLanscape from "components/Pdf/shipping_short_landscape.svg";
import ExportServices from "services/Export";
import ExportVendorPDF from "components/Pdf/export.vendor";
import FinanceServices from "services/Finance";

// *For Table Style
const Row = styled(TableRow)(({ theme }) => ({
  border: 0,
}));

const Cell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    fontFamily: FontFamily.NunitoRegular,
    border: 0,
    padding: "3px",
    textAlign: "center",
    // whiteSpace: 'nowrap',
    whiteSpace: "normal",
    wordWrap: "break-word",
    maxWidth: "56px",
    background: Colors.primary,
    color: Colors.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    fontFamily: FontFamily.NunitoRegular,
    textAlign: "center",
    textWrap: "nowrap",
    padding: "3px !important",

    ".MuiBox-root": {
      display: "flex",
      gap: "3px",
      alignItems: "center",
      justifyContent: "center",
      ".MuiBox-root": {
        cursor: "pointer",
      },
    },
    svg: {
      width: "auto",
      height: "14px",
    },
    ".MuiTypography-root": {
      textTransform: "capitalize",
      fontFamily: FontFamily.NunitoRegular,
      textWrap: "nowrap",
    },
    ".MuiButtonBase-root": {
      padding: "3px",
      width: "14px",
      height: "14px",
    },
  },
}));

const VendorAppliedFundsView = () => {
  const today = new Date();
  const currentDate = moment(today).format("DD-MMM-YY HH:mm");
  const contentRef = useRef();
  const { state } = useLocation();
  const { id } = useParams();
  const [totalCount, setTotalCount] = useState(0);
  const [pageLimit, setPageLimit] = useState(150);
  const [currentPage, setCurrentPage] = useState(1);
  const [loader, setLoader] = useState(false);
  const [shippingVault, setShippingVault] = useState();
  const [vehicleVault, setVehicleVault] = useState();
  const [walletBalance, setWalletBalance] = useState(0);
  const [unappliedFunds, setUnAppliedFunds] = useState(0);

  // *For Vendor Fund Approval
  const [vendorFundApprovalDetail, setVendorFundApprovalDetail] = useState();
  const [currencyExchangeRate, setCurrencyExchangeRate] = useState();

  const isBroker =
    vendorFundApprovalDetail?.vendor?.customerProfile?.type?.toLowerCase() ===
    "broker";
  const isAgent =
    vendorFundApprovalDetail?.vendor?.customerProfile?.type?.toLowerCase() ===
    "agent";
  const isPortAgent =
    vendorFundApprovalDetail?.vendor?.customerProfile?.type === "Port Agent";
  const isRecoveryAgent =
    vendorFundApprovalDetail?.vendor?.customerProfile?.type ===
    "Recovery Agent";

  const getVaultDashboard = async (page, limit, vendor_id) => {
    setLoader(true);
    try {
      const Page = page ? page : currentPage;
      const Limit = limit ? limit : pageLimit;
      setCurrentPage(Page);
      setPageLimit(Limit);
      let params = {
        page: Page,
        limit: Limit,
        vendor_id: vendor_id,
      };
      const {
        data: { detail },
      } = await FinanceServices.getVehicleSumLedgerExport(params);
      const vendorRecievable = detail?.rows[0].accounts?.find(
        (account) =>
          account.type_code === "L2" && account.primary_series === 50003
      );

      const vendorUnapplied = detail?.rows[0].accounts?.find(
        (account) =>
          account.type_code === "A2" && account.primary_series === 20014
      );

      const filterCustomer = detail?.clientVault?.filter((i) => {
        return i?.id == vendor_id;
      });
      const filteredWallet = filterCustomer[0]?.accounts[0];
      setVehicleVault(filteredWallet);
      setWalletBalance(
        filteredWallet?.nature === "credit"
          ? parseFloat(
              filteredWallet?.total_credit - filteredWallet?.total_debit
            ).toFixed(2)
          : parseFloat(
              filteredWallet?.total_debit - filteredWallet?.total_credit
            ).toFixed(2)
      );
      setTotalCount(detail?.count);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const tableHead = ["SR No."];

  // Date Column
  if (isBroker) {
    tableHead.push("CMR Date");
  } else if (isRecoveryAgent) {
    tableHead.push("Pickup Date");
  } else {
    tableHead.push("MF Date");
  }

  // CMR or Menifest #
  if (isBroker) {
    tableHead.push("CMR #");
  } else if (isAgent || isPortAgent) {
    tableHead.push("Menifest #");
  }

  tableHead.push("Model / Make / Color");
  tableHead.push("VIN#");

  // Charges Column
  if (isBroker) {
    tableHead.push("BR Charges");
  } else if (isRecoveryAgent) {
    tableHead.push("Pick From");
  } else if (isAgent) {
    tableHead.push("Agent Charges");
  } else {
    tableHead.push("In-Country Charges");
  }

  // Payable or Border
  if (isBroker || isPortAgent) {
    tableHead.push("Total Payable");
  } else if (isRecoveryAgent) {
    tableHead.push("Pick To");
  } else {
    tableHead.push("Border Charges");
  }

  // Paid / Applied Amount
  if (isBroker) {
    tableHead.push("Paid / Applied Amount");
  } else if (isRecoveryAgent) {
    tableHead.push("Recovery Charges");
  } else if (isAgent) {
    tableHead.push("Total Payable");
  } else {
    tableHead.push("Paid / Applied Amount");
  }

  // Balance or other
  if (isBroker) {
    tableHead.push("Balance");
  } else if (isAgent) {
    tableHead.push("Paid / Applied Date");
  } else if (isPortAgent) {
    tableHead.push("Balance");
  }

  // Dates or Past Due
  if (isBroker) {
    tableHead.push("Paid /  Applied Date");
  } else if (isRecoveryAgent) {
    tableHead.push("Total");
  } else if (isAgent) {
    tableHead.push("Past Due Days");
  } else {
    tableHead.push("Paid / Applied Date");
  }

  // Extra columns for roles
  if (isBroker || isPortAgent) {
    tableHead.push("Past Due Days");
  }

  if (isRecoveryAgent) {
    tableHead.push("Paid / Applied Amount");
    tableHead.push("Balance");
    tableHead.push("Paid / Applied Date");
    tableHead.push("Past Due Days");
  }

  // *For Get Vendor Fund Approval
  const getVendorAppliedFundsDetail = async () => {
    setLoader(true);
    try {
      let params = {
        applied_id: id,
      };
      const { data } = await ExportServices.getExportVendorAppliedFundView(
        params
      );
      setVendorFundApprovalDetail(data?.details);
      getVaultDashboard(1, pageLimit, data?.details?.vendor_id);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const getCurrencies = async (currency) => {
    try {
      let params = {
        detailed: true,
      };
      const { data } = await CurrencyServices.getCurrencies(params);
      if (state?.vendor?.currency == "aed") {
        setCurrencyExchangeRate(data.currencies[0].conversion_rate);
      } else if (state?.vendor?.currency == "cad") {
        setCurrencyExchangeRate(data.currencies[1].conversion_rate);
      } else {
        setCurrencyExchangeRate(data.currencies[2].conversion_rate);
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };

  useEffect(() => {
    getCurrencies();
    if (id) {
      getVendorAppliedFundsDetail();
    }
  }, [id]);
  const getDateDifference = (date1, date2) => {
    const diffTime = Math.abs(date2 - date1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const calculateDueBreakup = () => {
    const getDate = (item) =>
      new Date(
        isBroker
          ? item?.vehicle?.cmr_date
          : isRecoveryAgent
          ? item?.vehicle?.recovery?.update_recovery_date
          : item?.vehicle?.manifest_date
      );

    const getChargeDiff = (funds) =>
      parseFloat(
        isBroker
          ? funds?.broker_charges - funds?.broker_charged_paid
          : isPortAgent
          ? funds?.shifting_charges - funds?.shifting_charged_paid
          : funds?.recovery_charges - funds?.recovery_charged_paid
      );

    const below15Days = vendorFundApprovalDetail?.details?.filter(
      (item) => getDateDifference(getDate(item), new Date()) <= 15
    );

    const below16To30Days = vendorFundApprovalDetail?.details?.filter(
      (item) => {
        const diff = getDateDifference(getDate(item), new Date());
        return diff > 15 && diff <= 30;
      }
    );

    const over30Days = vendorFundApprovalDetail?.details?.filter(
      (item) => getDateDifference(getDate(item), new Date()) > 30
    );

    const below15DaysTotal = below15Days?.reduce(
      (acc, curr) => acc + getChargeDiff(curr?.funds),
      0
    );

    const below16To30DaysTotal = below16To30Days?.reduce(
      (acc, curr) => acc + getChargeDiff(curr?.funds),
      0
    );

    const over30DaysTotal = over30Days?.reduce(
      (acc, curr) => acc + getChargeDiff(curr?.funds),
      0
    );

    const total = below15DaysTotal + below16To30DaysTotal + over30DaysTotal;

    return {
      below15DaysTotal: CommaSeparator(below15DaysTotal?.toFixed(2)),
      below16To30DaysTotal: CommaSeparator(below16To30DaysTotal?.toFixed(2)),
      over30DaysTotal: CommaSeparator(over30DaysTotal?.toFixed(2)),
      total: CommaSeparator(total?.toFixed(2)),
    };
  };

  const dueBreakup = calculateDueBreakup();
  return (
    <>
      <Box sx={{ m: 4, mb: 2 }}>
        <Box
          sx={{
            textAlign: "right",
            p: 2,
            alignItems: "center",
            justifyContent: "flex-end",
            display: "flex",
            mt: 4,
          }}
        >
          <PrimaryButton
            title="Download PDF"
            onClick={() => handleExportWithComponent(contentRef)}
          />
        </Box>
        <PDFExport
          ref={contentRef}
          paperSize={["210mm", "400mm"]}
          dpi={96}
          landscape={true}
          keepTogether=".bottom-box"
          margin={{ bottom: 80 }}
          fileName="vendor fund detail"
        >
          <Box className="pdf-show" display="none">
            <ExportVendorPDF
              vendorFundApprovalDetail={vendorFundApprovalDetail}
              currencyExchangeRate={currencyExchangeRate}
              tableHead={tableHead}
              currentDate={currentDate}
              mode="landscape"
              detailList={vendorFundApprovalDetail?.details || []}
              reportType={true}
              isAgent={isAgent}
              isBroker={isBroker}
              isRecoveryAgent={isRecoveryAgent}
              isPortAgent={isPortAgent}
              dueBreakup={dueBreakup}
            />
          </Box>
        </PDFExport>

        <Box
          sx={{
            minHeight: "210mm",
            margin: "0 auto",
          }}
        >
          <Box sx={{ minHeight: "297mm", margin: "0 auto" }}>
            {" "}
            {/* A4 width in px */}
            {/* Header with dummy icon */}
            <Box
              component="img"
              src={ShippingLanscape}
              sx={{ marginBottom: "0px" }}
              width="100%"
            />
            <Box className="invoice">
              <Box className="invoice-container">
                <Grid
                  container
                  spacing={0}
                  sx={{
                    background: "#EFF8E7",
                    display: "flex",
                    justifyContent: "center",
                    px: 0,
                  }}
                >
                  <Grid item xs={4}>
                    <Box sx={{ marginLeft: "4px", marginTop: "4px" }}>
                      <Typography
                        sx={{
                          borderBottom: "1px solid black",
                          fontWeight: 700,
                          marginBottom: "12px",
                          fontFamily: FontFamily.openSans,
                          fontSize: "14px !important",
                        }}
                      >
                        Vendor Information:-
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          sx={{
                            fontSize: "12px !important",
                            fontWeight: 700,
                            width: "80px",
                            fontFamily: FontFamily.openSans,
                          }}
                        >
                          Name:
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "12px !important",
                            fontFamily: FontFamily.openSans,
                          }}
                        >
                          {vendorFundApprovalDetail?.vendor?.name ?? "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          sx={{
                            fontSize: "12px !important",
                            fontWeight: 700,
                            width: "80px",
                            fontFamily: FontFamily.openSans,
                          }}
                        >
                          Type:
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "12px !important",
                            fontFamily: FontFamily.openSans,
                          }}
                        >
                          {vendorFundApprovalDetail?.vendor?.customerProfile
                            ?.type
                            ? vendorFundApprovalDetail?.vendor?.customerProfile
                                ?.type === "Recovery Agent"
                              ? "Recovery & Shifitng Agent"
                              : vendorFundApprovalDetail?.vendor
                                  ?.customerProfile?.type
                            : "-" ?? "-"}{" "}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          sx={{
                            fontSize: "12px !important",
                            fontWeight: 700,
                            width: "80px",
                            fontFamily: FontFamily.openSans,
                          }}
                        >
                          Currency:
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "12px !important",
                            fontFamily: FontFamily.openSans,
                          }}
                        >
                          {vendorFundApprovalDetail?.currency
                            ? vendorFundApprovalDetail?.currency?.toUpperCase()
                            : "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{
                      display: "flex",
                      alignItems: "start",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FontFamily.openSans,
                        fontSize: "14px !important",
                        fontWeight: 700,
                      }}
                    >
                      Vendor Fund Detail
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Table
                      sx={{
                        width: "100%",
                        borderCollapse: "collapse",
                        marginBottom: "2px",
                      }}
                    >
                      <TableBody>
                        <TableRow>
                          {/* Left Column */}
                          <TableCell
                            sx={{ border: "1px solid", padding: "4px" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                Ext #
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                {vendorFundApprovalDetail?.tt_details
                                  ?.external_no ?? "-"}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Right Column */}
                          <TableCell
                            sx={{ border: "1px solid", padding: "4px" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                Exc Rate:
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                {vendorFundApprovalDetail?.tt_details
                                  ?.ex_rate ?? "-"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{ border: "1px solid", padding: "4px" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                FCY Amount:
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                {vendorFundApprovalDetail?.tt_details
                                  ?.fcy_amount ?? "-"}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell
                            sx={{ border: "1px solid", padding: "4px" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                LCY Amount:
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                {vendorFundApprovalDetail?.tt_details
                                  ?.lcy_amount ?? "-"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{ border: "1px solid", padding: "4px" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                TT Charges:
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                {vendorFundApprovalDetail?.tt_details
                                  ?.tt_charges ?? "-"}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell
                            sx={{ border: "1px solid", padding: "4px" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                VAT Charges:
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                {vendorFundApprovalDetail?.tt_details
                                  ?.vat_charges ?? "-"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{ border: "1px solid", padding: "4px" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                Total Amount:
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  fontFamily: FontFamily.openSans,
                                }}
                              >
                                {vendorFundApprovalDetail?.tt_details
                                  ?.total_amount ?? "-"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
              </Box>
            </Box>
            {/* Table Section */}
            <TableContainer
              component={Paper}
              sx={{
                overflowX: "auto",
                boxShadow: "0px 8px 18px 0px #9B9B9B1A",
                borderRadius: 2,
                width: "100%",
                "@media print": {
                  overflow: "visible",
                  marginLeft: "0px !important",
                },
              }}
            >
              <Table stickyHeader sx={{ minWidth: 2200, tableLayout: "auto" }}>
                <TableHead>
                  <TableRow>
                    {tableHead.map((item, index) => (
                      <Cell key={index}>{item}</Cell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendorFundApprovalDetail?.details?.length > 0 ? (
                    <Fragment>
                      {vendorFundApprovalDetail?.details.map((item, index) => {
                        const vehicle = item?.vehicle;
                        const funds = item?.funds;
                        const agentChargesTotal = (
                          parseFloat(funds?.agent_charged_paid || 0) +
                          parseFloat(funds?.border_charged_paid || 0)
                        ).toFixed(2);

                        const totapayable = isBroker
                          ? CommaSeparator(
                              parseFloat(funds?.broker_charged_paid).toFixed(2)
                            )
                          : isAgent
                          ? CommaSeparator(agentChargesTotal)
                          : isPortAgent
                          ? CommaSeparator(funds?.shifting_charged_paid)
                          : 0;
                        return (
                          <Row
                            key={index}
                            sx={{ bgcolor: index % 2 !== 0 && "#EEFBEE" }}
                          >
                            {/* SR No. */}
                            <Cell>{index + 1}</Cell>

                            {/* CMR Date / Pickup Date / MF Date */}
                            <Cell>
                              {vehicle
                                ? moment
                                    .utc(
                                      isBroker
                                        ? vehicle.cmr_date
                                        : isRecoveryAgent
                                        ? vehicle?.recovery
                                            ?.update_recovery_date
                                        : vehicle.manifest_date
                                    )
                                    .format("DD-MMM-YYYY")
                                : "-"}
                            </Cell>

                            {/* CMR # or Menifest # */}
                            {(isBroker || isAgent || isPortAgent) && (
                              <Cell>
                                {isBroker
                                  ? vehicle?.cmr_number ?? "-"
                                  : vehicle?.manifest_number ?? "-"}
                              </Cell>
                            )}

                            {/* Model / Make / Color */}
                            <Cell>
                              {[
                                vehicle?.model?.name,
                                vehicle?.make?.name,
                                vehicle?.color,
                              ]
                                .filter(Boolean)
                                .join(" / ") || "-"}
                            </Cell>

                            {/* VIN# */}
                            <Cell>{vehicle?.vin ?? "-"}</Cell>

                            {/* Charges (based on role) */}
                            <Cell>
                              {isBroker
                                ? parseFloat(funds?.broker_charges)?.toFixed(
                                    2
                                  ) ?? "-"
                                : isRecoveryAgent
                                ? vehicle?.pickup_from ?? "-"
                                : isAgent
                                ? parseFloat(funds?.agent_charges)?.toFixed(
                                    2
                                  ) ?? "-"
                                : parseFloat(funds?.shifting_charges)?.toFixed(
                                    2
                                  ) ?? "-"}
                            </Cell>

                            {/* Total Payable / Pick To / Border Charges */}
                            <Cell>
                              {isBroker || isPortAgent
                                ? totapayable ?? "-"
                                : isRecoveryAgent
                                ? vehicle?.destination?.name ?? "-"
                                : isAgent
                                ? funds?.border_charges
                                  ? parseFloat(funds?.border_charges)?.toFixed(
                                      2
                                    )
                                  : "-"
                                : "-" ?? "-"}
                            </Cell>

                            {/* Paid / Applied or Recovery or Total Payable */}
                            <Cell>
                              {isBroker || (!isAgent && !isRecoveryAgent)
                                ? `${
                                    parseFloat(item?.applied_amount)?.toFixed(
                                      2
                                    ) ?? "0.00"
                                  } `
                                : isRecoveryAgent
                                ? parseFloat(funds?.recovery_charges)?.toFixed(
                                    2
                                  ) ?? "-"
                                : totapayable ?? "-"}
                            </Cell>

                            {/* Balance or Shifting or Paid Date */}
                            <Cell>
                              {isBroker
                                ? parseFloat(
                                    funds?.broker_charges -
                                      funds?.broker_charged_paid
                                  )?.toFixed(2) ?? "-"
                                : isRecoveryAgent
                                ? parseFloat(funds?.recovery_charges)?.toFixed(
                                    2
                                  ) ?? "-"
                                : isAgent
                                ? item?.created_at
                                  ? moment(item?.created_at).format(
                                      "DD-MMM-YYYY"
                                    )
                                  : "-"
                                : parseFloat(
                                    funds?.shifting_charges -
                                      funds?.shifting_charged_paid
                                  )?.toFixed(2) ?? "-"}
                            </Cell>

                            {/* Paid / Applied Date / Total / Past Due */}
                            <Cell>
                              {isBroker
                                ? item?.created_at
                                  ? moment(item?.created_at).format(
                                      "DD-MMM-YYYY"
                                    )
                                  : "-"
                                : isRecoveryAgent
                                ? parseFloat(item?.applied_amount)?.toFixed(
                                    2
                                  ) ?? "-"
                                : isAgent
                                ? moment().diff(
                                    vehicle?.manifest_date,
                                    "days"
                                  ) ?? "-"
                                : item?.created_at
                                ? moment(item?.created_at).format("DD-MMM-YYYY")
                                : "-"}
                            </Cell>

                            {/* Extra column: Past Due for Broker/Port Agent */}
                            {(isBroker || isPortAgent) && (
                              <Cell>
                                {isPortAgent
                                  ? moment().diff(
                                      vehicle?.manifest_date,
                                      "days"
                                    ) || "-"
                                  : isBroker
                                  ? moment().diff(vehicle.cmr_date, "days") ||
                                    "-"
                                  : "-"}
                              </Cell>
                            )}

                            {/* Extra columns for Recovery Agent */}
                            {isRecoveryAgent && (
                              <>
                                <Cell>
                                  {`${
                                    parseFloat(
                                      funds?.recovery_charges -
                                        funds?.recovery_charged_paid
                                    )?.toFixed(2) ?? "0.00"
                                  }`}
                                </Cell>

                                <Cell>
                                  {item?.created_at
                                    ? moment(item?.created_at).format(
                                        "DD-MMM-YYYY"
                                      )
                                    : "-"}
                                </Cell>
                                <Cell>
                                  {moment().diff(
                                    vehicle?.recovery?.update_recovery_date,
                                    "days"
                                  ) || "-"}
                                </Cell>
                              </>
                            )}
                          </Row>
                        );
                      })}
                    </Fragment>
                  ) : (
                    <Row>
                      <Cell
                        colSpan={tableHead.length + 1}
                        align="center"
                        sx={{ fontWeight: 600 }}
                      >
                        No Data Found
                      </Cell>
                    </Row>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default VendorAppliedFundsView;
